import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { rest } from '@app/components/Api';
import {
	BOOKING_SCHEDULE,
	bookingRoutes,
	LS_CONTINUE_SESSION,
	LS_RESERVATION_START_TIME,
	LS_SCHEDULE_STEP,
	LS_SELECTED_OPTION,
} from '@app/components/LocalCommon/Utils/booking';
import { BookingContext, BookingErrorCode } from '@app/components/Pages/Booking/Booking';
import BookingModal from '@app/components/UI/Booking/BookingModal';
import { OnlineBookingEntry, OnlineBookingLastStep, ScheduleOption } from '@app/objects/OnlineBooking';

const secondsToEndTheSession = 600; // 10 min

const BookingReservAppointment: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const context = React.useContext(BookingContext);

	if (!context?.state) throw new Error('Error context');

	const {
		state: {
			item,
			errorCode,
			selectedOption,
			errorMessageReserve,
		},
		action: {
			setItem,
			setErrorCode,
			setSelectedOption,
			setIsLoadingReserve,
			setErrorMessageReserve,
		},
	} = context;

	const [firstRender, setFirstRender] = React.useState(true);
	const [timeNoLongerAvailableModal, setTimeNoLongerAvailableModal] = React.useState(false);
	const [openModalWarning, setOpenModalWarning] = React.useState(false);
	const [openModalInterruptingSession, setOpenModalInterruptingSession] = React.useState(false);
	const [continueSession, setContinueSession] = React.useState(false);
	const [timer, setTimer] = React.useState<NodeJS.Timeout>();
	const [remainingTime, setRemainingTime] = React.useState<string>('0:00');
	const [reservationStartTime, setReservationStartTime] = React.useState<number | null>(null);

	const getFinishTime = (timestamp: number, seconds: number = secondsToEndTheSession): number => {
		return new Date(timestamp).setUTCSeconds(new Date(timestamp).getUTCSeconds() + seconds);
	};

	const startTimer = (timestamp: number) => {
		const actualStart = timestamp || Date.now();
		localStorage.setItem(LS_RESERVATION_START_TIME, JSON.stringify(actualStart));
		setReservationStartTime(actualStart);
	};

	const removeTimer = (resetContinueSession: boolean) => {
		if (resetContinueSession) {
			setContinueSession(false);
			localStorage.removeItem(LS_CONTINUE_SESSION);
		}

		clearInterval(timer);
		setRemainingTime('0:00');
		setReservationStartTime(null);
		setTimer(undefined);
		localStorage.removeItem(LS_RESERVATION_START_TIME);
	};

	const reserveAnAppointment = (data: { date: number; veterinarianId: number; areaId: number; selected: boolean }) => {
		if (!item) return;
		setIsLoadingReserve(true);

		rest.post<any>(`v1/booking/schedule/${item.guid}/reserve`, data)
			.then((res) => {
				if (res?.errorCode && res.errorCode !== BookingErrorCode.ValidationError) {
					setErrorCode(res.errorCode);
				} else if (res?.error?.length || !res) {
					setErrorMessageReserve(res?.error || 'Service unavailable. Please try again later.');
				}
			})
			.catch((err) => setErrorMessageReserve(err))
			.finally(() => setIsLoadingReserve(false));
	};

	const handleTimeoutOrIncompleteData = () => {
		setOpenModalWarning(false);
		setOpenModalInterruptingSession(true);
		setSelectedOption(null);
		setReservationStartTime(null);
		localStorage.removeItem(LS_SCHEDULE_STEP);

		if (item && item.lastStep > OnlineBookingLastStep.Schedule) {
			const newBooking: OnlineBookingEntry = { ...item, lastStep: OnlineBookingLastStep.Schedule };
			setItem(newBooking);

			for (const key in bookingRoutes) {
				if (bookingRoutes[key] === location.pathname) {
					if (Number(key) > newBooking.lastStep) {
						navigate(BOOKING_SCHEDULE);
					}
					break;
				}
			}
		}
	};

	React.useEffect(() => {
		const localSelectedOption: ScheduleOption | null = JSON.parse(localStorage[LS_SELECTED_OPTION] || 'null');
		const localReservationStartTime: number | null = JSON.parse(localStorage[LS_RESERVATION_START_TIME] || 'null');
		const localContinueSession: boolean = JSON.parse(localStorage[LS_CONTINUE_SESSION] || 'false');
		const expired: boolean = localReservationStartTime ? Date.now() >= getFinishTime(localReservationStartTime) : false;

		if (localSelectedOption && localReservationStartTime && !expired) {
			setSelectedOption(localSelectedOption);
			setReservationStartTime(localReservationStartTime);
			setContinueSession(localContinueSession);
		} else {
			expired && setErrorCode(BookingErrorCode.IncompleteBookingData);
			localStorage.removeItem(LS_SELECTED_OPTION);
			localStorage.removeItem(LS_RESERVATION_START_TIME);
			localStorage.removeItem(LS_CONTINUE_SESSION);
		}

		setFirstRender(false);

		return () => clearInterval(timer);
	}, []);

	React.useEffect(() => {
		if (item && (item.lastStep < OnlineBookingLastStep.Schedule || item.lastStep === OnlineBookingLastStep.Confirmation)) {
			if (selectedOption && (item.selectedDate || item.selectedUserId)) {
				reserveAnAppointment({
					date: selectedOption.date,
					veterinarianId: selectedOption.veterinarian.id,
					areaId: selectedOption.areaId,
					selected: false,
				});
			}

			removeTimer(true);
			setSelectedOption(null);
		}
	}, [item?.lastStep]);

	React.useEffect(() => {
		if (errorCode === BookingErrorCode.TimeNoLongerAvailable) {
			setSelectedOption(null);
			setTimeNoLongerAvailableModal(true);
			localStorage.removeItem(LS_SCHEDULE_STEP);
			navigate(BOOKING_SCHEDULE);
		} else if (errorCode === BookingErrorCode.IncompleteBookingData) {
			handleTimeoutOrIncompleteData();
		}
	}, [errorCode]);

	React.useEffect(() => {
		if (!firstRender && reservationStartTime) {
			const now = Date.now();
			const halfSession = getFinishTime(reservationStartTime, secondsToEndTheSession / 2);
			const sessionEnd = getFinishTime(reservationStartTime);

			const timeoutWarning = setTimeout(() => {
				setOpenModalWarning(true);
			}, halfSession - now);

			const timeoutEnd = setTimeout(() => {
				handleTimeoutOrIncompleteData();
			}, sessionEnd - now);

			return () => {
				clearTimeout(timeoutWarning);
				clearTimeout(timeoutEnd);
			};
		}
	}, [reservationStartTime]);

	React.useEffect(() => {
		if (!firstRender) {
			if (timer) removeTimer(true);
			if (selectedOption) {
				const timestamp = JSON.parse(localStorage[LS_RESERVATION_START_TIME] || Date.now());
				startTimer(timestamp || Date.now());
			}
			localStorage.setItem(LS_SELECTED_OPTION, JSON.stringify(selectedOption));
		}
	}, [selectedOption]);

	React.useEffect(() => {
		if (!JSON.parse(localStorage[LS_CONTINUE_SESSION] || 'false') && continueSession) {
			removeTimer(false);
			startTimer(Date.now());
			localStorage.setItem(LS_CONTINUE_SESSION, JSON.stringify(continueSession));
		}
	}, [continueSession]);

	React.useEffect(() => {
		if (!openModalWarning || !reservationStartTime) return;

		const updateTime = () => {
			const diff = Math.max(0, Math.floor((getFinishTime(reservationStartTime) - Date.now()) / 1000));
			const minutes = Math.floor(diff / 60);
			const seconds = String(diff % 60).padStart(2, '0');
			setRemainingTime(`${minutes}:${seconds}`);
		};

		updateTime();

		const interval = setInterval(updateTime, 1000);

		return () => clearInterval(interval);
	}, [openModalWarning, reservationStartTime]);

	return (
		<>
			{timeNoLongerAvailableModal && (
				<BookingModal
					open={timeNoLongerAvailableModal}
					onOk={() => {
						setErrorCode(null);
						setTimeNoLongerAvailableModal(false);
					}}
					title="Time No Longer Available"
					buttonText="Return to Scheduling"
				>
					<div>
						We’re sorry. This time is already taken. Please click below to select a new time and date. If you need immediate assistance,
						please call us at
						{' '}
						<a href="tel:8559335683" className="inline-link inline-block">(855) 933-5683.</a>
					</div>
				</BookingModal>
			)}

			{openModalWarning && (
				<BookingModal
					open={openModalWarning}
					title="Session Timeout"
					onOk={() => {
						if (!continueSession && selectedOption) {
							reserveAnAppointment({
								date: selectedOption.date,
								veterinarianId: selectedOption.veterinarian.id,
								areaId: selectedOption.areaId,
								selected: true,
							});
							setContinueSession(true);
						}
						setOpenModalWarning(false);
					}}
					buttonText="Continue Session"
				>
					<div>
						<p>Your session will expire in:</p>
						<p className="text-bold">{remainingTime}</p>
						<p>If your session expires, we won't be able to hold your appointment date and time.</p>
					</div>
				</BookingModal>
			)}

			{openModalInterruptingSession && (
				<BookingModal
					open={openModalInterruptingSession}
					title="Session Timed Out"
					onOk={() => {
						removeTimer(true);
						setErrorCode(null);
						setOpenModalInterruptingSession(false);
						navigate(BOOKING_SCHEDULE);
					}}
					buttonText="Return to Scheduling"
				>
					<div>
						We’re sorry. Your session has expired. Please click below to select a new time and date. If you need immediate assistance,
						please call us at
						{' '}
						<a href="tel:8559335683" className="inline-link inline-block">(855) 933-5683.</a>
					</div>
				</BookingModal>
			)}

			{errorMessageReserve?.length ? (
				<BookingModal
					open={Boolean(errorMessageReserve.length)}
					title="Time reservation error"
					buttonText="Ok"
					onOk={() => setErrorMessageReserve('')}
				>
					<div>{errorMessageReserve}</div>
				</BookingModal>
			) : null}
		</>
	);
};

export default BookingReservAppointment;
